/** SUNTRONIX ONE — WhatsApp Business Marketing & Bulk Campaign Engine (Phase A).
 *  Reuses ONE Engage segments/consent/suppression and the official Meta Cloud API.
 */
import { useCallback, useEffect, useState } from "react";
import {
  MessageCircle, Send, FileText, ShieldBan, Settings2, Plus, Trash2, Eye,
  Loader2, AlertTriangle, Pause, Square, Play, FlaskConical, RefreshCw,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const money = (n, c = "INR") => `${c === "INR" ? "\u20B9" : c + " "}${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const TABS = [
  { id: "campaigns", label: "Campaigns", icon: Send },
  { id: "templates", label: "Marketing Templates", icon: FileText },
  { id: "suppression", label: "Opt-out & Suppression", icon: ShieldBan },
  { id: "settings", label: "Configuration", icon: Settings2 },
];
const STATUS_TONE = {
  DRAFT: "bg-slate-100 text-slate-700", SCHEDULED: "bg-indigo-100 text-indigo-800",
  QUEUED: "bg-amber-100 text-amber-800", SENDING: "bg-sky-100 text-sky-800",
  SENT: "bg-emerald-100 text-emerald-800", PAUSED: "bg-orange-100 text-orange-800",
  STOPPED: "bg-rose-100 text-rose-800", PREPARED_SETUP_REQUIRED: "bg-violet-100 text-violet-800",
  APPROVED: "bg-emerald-100 text-emerald-800", PENDING: "bg-amber-100 text-amber-800",
  REJECTED: "bg-rose-100 text-rose-800", PAUSED_T: "bg-orange-100 text-orange-800",
};

export default function WhatsAppCampaigns() {
  const [tab, setTab] = useState("campaigns");
  const [status, setStatus] = useState(null);
  useEffect(() => { api.get("/wa-campaigns/status").then(({ data }) => setStatus(data)).catch(() => {}); }, []);
  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="wa-campaigns-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">ONE Engage · Campaigns</div>
          <h1 className="text-2xl font-heading font-bold text-slate-900 flex items-center gap-2">
            <MessageCircle className="h-6 w-6 text-emerald-600" /> WhatsApp Marketing
          </h1>
          <p className="text-sm text-slate-500 mt-1">Authorised WhatsApp Business Platform campaigns to eligible, opted-in contacts.</p>
        </div>
        {status && (
          <Badge className={status.send_ready ? "bg-emerald-100 text-emerald-800" : "bg-violet-100 text-violet-800"} data-testid="wa-mode-badge">
            {status.send_ready ? "LIVE" : "SETUP REQUIRED"} · Graph {status.graph_version}
          </Badge>
        )}
      </div>

      {status && !status.send_ready && (
        <div className="mt-4 flex items-start gap-2 text-[12px] text-violet-900 bg-violet-50 border border-violet-200 rounded-md px-3 py-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          Running in preview mode. Add WHATSAPP_WABA_ID and the Meta credentials to enable live template management and sending. Campaigns you approve are prepared and queued safely until then.
        </div>
      )}

      <div className="mt-5 flex gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`wa-tab-${t.id}`}
            className={`px-4 py-2 text-[13px] font-medium border-b-2 -mb-px flex items-center gap-1.5 ${tab === t.id ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {tab === "campaigns" && <CampaignsTab status={status} />}
        {tab === "templates" && <TemplatesTab status={status} />}
        {tab === "suppression" && <SuppressionTab />}
        {tab === "settings" && <SettingsTab />}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Campaigns */
function CampaignsTab({ status }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [previewFor, setPreviewFor] = useState(null);
  const load = useCallback(() => {
    setLoading(true);
    api.get("/wa-campaigns/campaigns").then(({ data }) => setItems(data.items || [])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button onClick={() => setShowNew(true)} data-testid="wa-new-campaign-btn" className="bg-emerald-700">
          <Plus className="h-4 w-4 mr-1.5" /> New WhatsApp Campaign
        </Button>
      </div>
      {loading ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : items.length === 0 ? (
        <div className="text-[13px] text-slate-400 border border-dashed rounded-lg py-10 text-center">No campaigns yet.</div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
              <tr><th className="text-left px-3 py-2">Campaign</th><th className="text-left px-3 py-2">Type</th>
                <th className="text-right px-3 py-2">Audience</th><th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Actions</th></tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t border-slate-100" data-testid={`wa-camp-row-${c.id}`}>
                  <td className="px-3 py-2 font-medium">{c.name}</td>
                  <td className="px-3 py-2 text-slate-500">{c.type}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{c.queued_count ?? (c.audience?.final_send ?? "—")}</td>
                  <td className="px-3 py-2"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${STATUS_TONE[c.status] || "bg-slate-100"}`}>{c.status}</span></td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setPreviewFor(c.id)} className="text-emerald-700 inline-flex items-center gap-1" data-testid={`wa-camp-open-${c.id}`}>
                      <Eye className="h-4 w-4" /> Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showNew && <NewCampaignDialog status={status} onClose={() => setShowNew(false)} onDone={() => { setShowNew(false); load(); }} />}
      {previewFor && <CampaignDrawer cid={previewFor} status={status} onClose={() => { setPreviewFor(null); load(); }} />}
    </div>
  );
}

function NewCampaignDialog({ status, onClose, onDone }) {
  const [cfg, setCfg] = useState(null);
  const [segments, setSegments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [f, setF] = useState({ name: "", type: "CORPORATE_GIFTING", segment_id: "", template_id: "", body_params: "{{contact_name}}", cta_url: "", header_media_url: "", button_url_suffix: "", schedule_at: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/wa-campaigns/config").then(({ data }) => setCfg(data));
    api.get("/engage/segments").then(({ data }) => setSegments(data.items || []));
    api.get("/wa-campaigns/templates").then(({ data }) => setTemplates(data.items || []));
  }, []);
  const set = (p) => setF((s) => ({ ...s, ...p }));
  const save = async () => {
    if (!f.name.trim()) return toast.error("Campaign name required");
    setBusy(true);
    try {
      await api.post("/wa-campaigns/campaigns", {
        ...f, body_params: f.body_params ? f.body_params.split(",").map((x) => x.trim()).filter(Boolean) : [],
        schedule_at: f.schedule_at || null,
      });
      toast.success("Campaign created");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="wa-new-campaign-dialog">
        <DialogHeader><DialogTitle>New WhatsApp Campaign</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Campaign name"><Input value={f.name} onChange={(e) => set({ name: e.target.value })} data-testid="wa-nc-name" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type"><Select value={f.type} onChange={(v) => set({ type: v })} options={(cfg?.campaign_types || []).map((t) => [t, t])} testid="wa-nc-type" /></Field>
            <Field label="Language"><Input value={f.language || cfg?.default_language || "en_US"} onChange={(e) => set({ language: e.target.value })} /></Field>
          </div>
          <Field label="Audience segment"><Select value={f.segment_id} onChange={(v) => set({ segment_id: v })} options={[["", "— pick a segment —"], ...segments.map((s) => [s.id, s.name])]} testid="wa-nc-segment" /></Field>
          <Field label="Approved template"><Select value={f.template_id} onChange={(v) => set({ template_id: v })} options={[["", "— pick a template —"], ...templates.map((t) => [t.id, `${t.name} (${t.status})`])]} testid="wa-nc-template" /></Field>
          <Field label="Body variables (comma separated; use {{contact_name}} / {{company}})">
            <Input value={f.body_params} onChange={(e) => set({ body_params: e.target.value })} data-testid="wa-nc-bodyparams" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="CTA URL"><Input value={f.cta_url} onChange={(e) => set({ cta_url: e.target.value })} /></Field>
            <Field label="Header media URL (optional)"><Input value={f.header_media_url} onChange={(e) => set({ header_media_url: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Button URL suffix (optional)"><Input value={f.button_url_suffix} onChange={(e) => set({ button_url_suffix: e.target.value })} /></Field>
            <Field label="Schedule at (optional)"><Input type="datetime-local" value={f.schedule_at} onChange={(e) => set({ schedule_at: e.target.value })} /></Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-emerald-700" data-testid="wa-nc-save">{busy ? "Saving…" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CampaignDrawer({ cid, status, onClose }) {
  const [p, setP] = useState(null);
  const [recip, setRecip] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/wa-campaigns/campaigns/${cid}/preview`).then(({ data }) => setP(data)).catch(() => {});
    api.get(`/wa-campaigns/campaigns/${cid}/recipients`).then(({ data }) => setRecip(data)).catch(() => {});
  }, [cid]);
  useEffect(() => { load(); }, [load]);
  const act = async (path, ok) => {
    setBusy(true);
    try { const { data } = await api.post(`/wa-campaigns/campaigns/${cid}/${path}`); toast.success(ok || data.status); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/wa-campaigns/campaigns/${cid}/test`, {}); toast.success(`Test: ${data.status} → ${data.to}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); } finally { setBusy(false); }
  };
  const a = p?.audience || {};
  const funnel = [
    ["Total contacts", a.total], ["Valid WhatsApp numbers", a.valid_whatsapp], ["Marketing eligible", a.marketing_eligible],
    ["Opted out", a.opted_out], ["Do not contact", a.do_not_contact], ["Suppressed", a.suppressed],
    ["Not opted-in (no consent)", a.not_opted_in],
    ["Invalid / missing mobile", a.invalid_missing_mobile], ["Frequency limited", a.frequency_limited],
    ["Duplicates", a.duplicates],
  ];
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="wa-camp-drawer">
        {!p ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">{p.campaign.name}
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${STATUS_TONE[p.campaign.status] || "bg-slate-100"}`}>{p.campaign.status}</span>
              </DialogTitle>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Eligibility</div>
                <div className="border rounded-lg divide-y">
                  {funnel.map(([k, v]) => (
                    <div key={k} className="flex justify-between px-3 py-1.5 text-[12px]"><span className="text-slate-600">{k}</span><span className="tabular-nums font-medium">{v ?? 0}</span></div>
                  ))}
                  <div className="flex justify-between px-3 py-2 text-[13px] bg-emerald-50">
                    <span className="font-semibold text-emerald-900">Final send audience</span>
                    <span className="tabular-nums font-bold text-emerald-900" data-testid="wa-final-send">{a.final_send ?? 0}</span>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Template</div>
                  <div className="text-[13px] font-medium">{p.template.name || "—"}
                    <span className={`ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded ${STATUS_TONE[p.template.status] || "bg-slate-100"}`}>{p.template.status}</span>
                  </div>
                  {p.template.body_text && <div className="text-[12px] text-slate-600 mt-1 bg-slate-50 rounded p-2 whitespace-pre-wrap">{p.template.body_text}</div>}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Estimated cost</div>
                  <div className="text-[15px] font-bold">{money(p.cost.estimated_total, p.cost.currency)}
                    <span className="ml-1 text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">ESTIMATE</span>
                  </div>
                  <div className="text-[11px] text-slate-500">{p.cost.recipients} × {money(p.cost.unit_rate, p.cost.currency)} · {p.cost.rate_card_version}</div>
                </div>
                {p.unresolved_variables && (
                  <div className="text-[11px] text-rose-800 bg-rose-50 border border-rose-200 rounded px-2 py-1.5 flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" /> Unresolved template variables — cannot send.
                  </div>
                )}
                {recip && Object.keys(recip.counts).length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Send queue</div>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(recip.counts).map(([k, v]) => (
                        <span key={k} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${STATUS_TONE[k] || "bg-slate-100"}`}>{k} {v}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <DialogFooter className="flex-wrap gap-2">
              <Button variant="outline" onClick={test} disabled={busy} data-testid="wa-test-btn"><FlaskConical className="h-4 w-4 mr-1.5" /> Test send</Button>
              {["DRAFT", "PREPARED_SETUP_REQUIRED"].includes(p.campaign.status) && (
                <Button onClick={() => act("send", "Campaign approved & queued")} disabled={busy || a.final_send === 0} className="bg-emerald-700" data-testid="wa-send-btn">
                  <Send className="h-4 w-4 mr-1.5" /> Approve &amp; send{status && !status.send_ready ? " (prepare)" : ""}
                </Button>
              )}
              {p.campaign.status === "SENDING" && (
                <>
                  <Button variant="outline" onClick={() => act("pause", "Paused")} disabled={busy}><Pause className="h-4 w-4 mr-1.5" /> Pause</Button>
                  <Button variant="outline" onClick={() => act("stop", "Stopped")} disabled={busy}><Square className="h-4 w-4 mr-1.5" /> Stop</Button>
                </>
              )}
              {["PAUSED", "SCHEDULED"].includes(p.campaign.status) && (
                <Button onClick={() => act("resume", "Resumed")} disabled={busy} className="bg-emerald-700"><Play className="h-4 w-4 mr-1.5" /> Resume</Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ Templates */
function TemplatesTab({ status }) {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const load = useCallback((sync) => {
    if (sync) setSyncing(true);
    api.get(`/wa-campaigns/templates${sync ? "?sync=true" : ""}`).then(({ data }) => setItems(data.items || [])).finally(() => setSyncing(false));
  }, []);
  useEffect(() => { load(false); }, [load]);
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div className="text-[12px] text-slate-500">Only APPROVED marketing templates can be used for production campaigns.</div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => load(true)} disabled={syncing || !status?.management_ready} data-testid="wa-tpl-sync">
            {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Sync from Meta
          </Button>
          <Button onClick={() => setShowNew(true)} className="bg-emerald-700" data-testid="wa-tpl-new"><Plus className="h-4 w-4 mr-1.5" /> New template</Button>
        </div>
      </div>
      {items.length === 0 ? <div className="text-[13px] text-slate-400 border border-dashed rounded-lg py-10 text-center">No templates yet.</div> : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500"><tr>
              <th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Category</th>
              <th className="text-left px-3 py-2">Lang</th><th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2"></th></tr></thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id} className="border-t border-slate-100" data-testid={`wa-tpl-row-${t.id}`}>
                  <td className="px-3 py-2 font-mono">{t.name}</td>
                  <td className="px-3 py-2 text-slate-500">{t.category}</td>
                  <td className="px-3 py-2 text-slate-500">{t.language}</td>
                  <td className="px-3 py-2"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${STATUS_TONE[t.status] || "bg-slate-100"}`}>{t.status}</span></td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => api.delete(`/wa-campaigns/templates/${t.id}`).then(() => load(false))} className="text-rose-600"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showNew && <NewTemplateDialog onClose={() => setShowNew(false)} onDone={() => { setShowNew(false); load(false); }} />}
    </div>
  );
}

function NewTemplateDialog({ onClose, onDone }) {
  const [f, setF] = useState({ name: "", language: "en_US", category: "MARKETING", body_text: "", footer_text: "", header_format: "NONE" });
  const [busy, setBusy] = useState(false);
  const set = (p) => setF((s) => ({ ...s, ...p }));
  const save = async () => {
    if (!f.name.trim() || !f.body_text.trim()) return toast.error("Name and body required");
    setBusy(true);
    try { const { data } = await api.post("/wa-campaigns/templates", f); toast.success(`Template ${data.status}`); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="wa-new-template-dialog">
        <DialogHeader><DialogTitle>New Marketing Template</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Template name"><Input value={f.name} onChange={(e) => set({ name: e.target.value })} placeholder="diwali_corporate_gifting" data-testid="wa-nt-name" /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Category"><Select value={f.category} onChange={(v) => set({ category: v })} options={[["MARKETING", "Marketing"], ["UTILITY", "Utility"], ["AUTHENTICATION", "Authentication"]]} /></Field>
            <Field label="Language"><Input value={f.language} onChange={(e) => set({ language: e.target.value })} /></Field>
            <Field label="Header"><Select value={f.header_format} onChange={(v) => set({ header_format: v })} options={[["NONE", "None"], ["TEXT", "Text"], ["IMAGE", "Image"], ["VIDEO", "Video"], ["DOCUMENT", "Document"]]} /></Field>
          </div>
          <Field label="Body (use {{1}} for variables)"><textarea value={f.body_text} onChange={(e) => set({ body_text: e.target.value })} rows={3} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="wa-nt-body" /></Field>
          <Field label="Footer (optional)"><Input value={f.footer_text} onChange={(e) => set({ footer_text: e.target.value })} placeholder="Reply STOP to opt out" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-emerald-700" data-testid="wa-nt-save">{busy ? "Saving…" : "Create / Submit"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ Suppression */
function SuppressionTab() {
  const [items, setItems] = useState([]);
  const [phone, setPhone] = useState("");
  const load = useCallback(() => api.get("/wa-campaigns/suppression").then(({ data }) => setItems(data.items || [])), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!phone.trim()) return;
    try { await api.post("/wa-campaigns/suppression", { phone, source: "MANUAL" }); toast.success("Added to suppression"); setPhone(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="max-w-2xl">
      <div className="text-[12px] text-slate-500 mb-3">Numbers here never receive marketing. Inbound STOP replies (Phase B) will add here automatically.</div>
      <div className="flex gap-2 mb-4">
        <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (e.g. 919840068999)" data-testid="wa-supp-phone" />
        <Button onClick={add} className="bg-emerald-700" data-testid="wa-supp-add"><Plus className="h-4 w-4 mr-1.5" /> Suppress</Button>
      </div>
      {items.length === 0 ? <div className="text-[13px] text-slate-400 border border-dashed rounded-lg py-8 text-center">No suppressed numbers.</div> : (
        <div className="border rounded-lg divide-y">
          {items.map((s) => (
            <div key={s.phone} className="flex justify-between px-3 py-2 text-[13px]">
              <span className="font-mono">{s.phone}</span>
              <span className="text-slate-400 text-[11px]">{s.reason} · {s.source}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Settings */
function SettingsTab() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/wa-campaigns/config").then(({ data }) => setCfg(data)); }, []);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/wa-campaigns/config", {
        marketing_frequency_cap: Number(cfg.marketing_frequency_cap),
        frequency_window_days: Number(cfg.frequency_window_days),
        default_country_code: String(cfg.default_country_code),
        test_numbers: typeof cfg.test_numbers === "string" ? cfg.test_numbers.split(",").map((x) => x.trim()) : cfg.test_numbers,
        rate_card: cfg.rate_card, currency: cfg.currency, cost_limit: Number(cfg.cost_limit),
        rate_card_version: cfg.rate_card_version,
      });
      toast.success("Configuration saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  if (!cfg) return <Loader2 className="h-5 w-5 animate-spin" />;
  const set = (p) => setCfg((s) => ({ ...s, ...p }));
  return (
    <div className="max-w-lg space-y-3" data-testid="wa-settings">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Marketing frequency cap"><Input type="number" value={cfg.marketing_frequency_cap} onChange={(e) => set({ marketing_frequency_cap: e.target.value })} data-testid="wa-cfg-cap" /></Field>
        <Field label="Frequency window (days)"><Input type="number" value={cfg.frequency_window_days} onChange={(e) => set({ frequency_window_days: e.target.value })} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Default country code"><Input value={cfg.default_country_code} onChange={(e) => set({ default_country_code: e.target.value })} /></Field>
        <Field label="Currency"><Input value={cfg.currency} onChange={(e) => set({ currency: e.target.value })} /></Field>
      </div>
      <Field label="Test numbers (comma separated)">
        <Input value={Array.isArray(cfg.test_numbers) ? cfg.test_numbers.join(", ") : cfg.test_numbers} onChange={(e) => set({ test_numbers: e.target.value })} data-testid="wa-cfg-test" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label={`Marketing rate — ${cfg.default_country_code} (ESTIMATE)`}>
          <Input type="number" step="0.01" value={cfg.rate_card?.[cfg.default_country_code] ?? cfg.rate_card?.default ?? 0}
            onChange={(e) => set({ rate_card: { ...cfg.rate_card, [cfg.default_country_code]: Number(e.target.value) } })} data-testid="wa-cfg-rate" />
        </Field>
        <Field label="Cost limit per campaign (0 = none)"><Input type="number" value={cfg.cost_limit} onChange={(e) => set({ cost_limit: e.target.value })} /></Field>
      </div>
      <Button onClick={save} disabled={busy} className="bg-emerald-700" data-testid="wa-cfg-save">{busy ? "Saving…" : "Save configuration"}</Button>
    </div>
  );
}

/* ------------------------------------------------------------------ helpers */
function Field({ label, children }) {
  return <label className="block"><div className="text-[11px] text-slate-500 mb-1">{label}</div>{children}</label>;
}
function Select({ value, onChange, options, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full border rounded h-9 px-2 text-[13px] bg-white">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}
